import * as i0 from '@angular/core';
import { Injectable, Inject } from '@angular/core';
import * as i1 from '@angular/platform-browser';
import * as i2 from '@upr-web-primer/locale';
import { WINDOW } from '@upr-web-primer/window';
import { get } from 'lodash-es';

class SeoService {
    constructor(meta, title, window, defaultValues, localeService) {
        this.meta = meta;
        this.title = title;
        this.window = window;
        this.defaultValues = defaultValues;
        this.localeService = localeService;
    }
    setPageSEO(contentIntro) {
        let canonicalUrl = get(contentIntro, 'Component.MetadataFields.SEO.EmbeddedValues.0.SEOCanonicalURL.Values.0') ||
            get(contentIntro, 'Component.MetadataFields.SEOCanonicalURL.Values[0]') ||
            '';
        canonicalUrl = this.createCanonicalURL(canonicalUrl);
        const seoMeta = get(contentIntro, 'Component.MetadataFields.SEO.EmbeddedValues[0]') ||
            get(contentIntro, 'Component.MetadataFields') ||
            '';
        const defaultMetadata = {
            title: get(seoMeta, 'SEOTitle.Values[0]') ||
                get(this.defaultValues, 'SEO_DEFAULT_TITLE'),
            description: get(seoMeta, 'SEODescription.Values[0]') ||
                get(this.defaultValues, 'SEO_DEFAULT_DESCRIPTION'),
            image: get(this.defaultValues, 'SEO_DEFAULT_IMAGE_URL'),
            facebookSiteName: get(this.defaultValues, 'SEO_DEFAULT_FACEBOOK_SITE_NAME'),
            twitterSiteName: get(this.defaultValues, 'SEO_DEFAULT_TWITTER_SITE_NAME'),
        };
        this.setPageMeta(get(seoMeta, 'SEOTitle.Values[0]', ''), get(seoMeta, 'SEODescription.Values[0]', ''), get(seoMeta, 'SEOKeywords.Values[0]', ''), get(seoMeta, 'Robots.Values[0]', '') ||
            get(seoMeta, 'robots.Values[0]', ''));
        const fbMeta = get(contentIntro, 'Component.MetadataFields.SEO.EmbeddedValues[0].OpenGraph.EmbeddedValues[0]') ||
            get(contentIntro, 'Component.MetadataFields.OpenGraph.EmbeddedValues[0]') ||
            '';
        const twitterMeta = get(contentIntro, 'Component.MetadataFields.Details.EmbeddedValues[0].Twitter.EmbeddedValues[0]') ||
            get(contentIntro, 'Component.MetadataFields.Twitter.EmbeddedValues[0]') ||
            get(contentIntro, 'Component.MetadataFields.TwitterCard.EmbeddedValues[0]') ||
            '';
        const location = {
            latitude: get(contentIntro, 'Component.MetadataFields.Details.EmbeddedValues[0].MapLatitude.Values[0]', ''),
            longitude: get(contentIntro, 'Component.MetadataFields.Details.EmbeddedValues[0].MapLongitude.Values[0]', ''),
        };
        this.setSocialMeta(fbMeta, twitterMeta, location, canonicalUrl, defaultMetadata);
    }
    setPageMeta(title, description, keywords, robots) {
        if (title) {
            this.title.setTitle(title);
            this.meta.updateTag({
                name: 'apple-mobile-web-app-title',
                content: title,
            });
        }
        if (description) {
            this.meta.updateTag({ name: 'description', content: description });
        }
        if (keywords) {
            this.meta.updateTag({ name: 'keywords', content: keywords });
        }
        if (robots) {
            this.meta.updateTag({ name: 'robots', content: robots });
        }
    }
    createCanonicalURL(url) {
        const canonical = this.window.document.querySelector('link[rel="canonical"]');
        let canonicalUrl = (url || this.window.document.URL || '').toLowerCase();
        if (!canonicalUrl) {
            return canonicalUrl;
        }
        const hash = get(this.window, 'location.hash');
        if (hash) {
            canonicalUrl = canonicalUrl.replace(hash, '');
        }
        const search = get(this.window, 'location.search');
        if (search) {
            canonicalUrl = canonicalUrl.replace(search, '');
        }
        if (canonicalUrl.indexOf('index.html') > -1) {
            canonicalUrl = canonicalUrl.replace('/index.html', '');
        }
        if (canonical) {
            canonical.setAttribute('href', canonicalUrl);
        }
        else {
            const link = this.window.document.createElement('link');
            link.setAttribute('rel', 'canonical');
            this.window.document.head.appendChild(link);
            link.setAttribute('href', canonicalUrl);
        }
        return canonicalUrl;
    }
    setSocialMeta(fbMeta, twitterMeta, location, canonicalUrl, defaultMetadata) {
        const locale = this.localeService.getFromUrl();
        if (locale && locale.language) {
            this.meta.updateTag({ property: 'og:locale', content: locale.language });
        }
        if (fbMeta) {
            const ogTitle = get(fbMeta, 'Title.Values[0]') || get(defaultMetadata, 'title') || '';
            const ogDescription = get(fbMeta, 'Description.Values[0]') ||
                get(defaultMetadata, 'description') ||
                '';
            const ogType = get(fbMeta, 'Type.Values[0]') || 'website';
            const ogImageUrl = get(fbMeta, 'Image.LinkedComponentValues[0].Multimedia.Url', '');
            const ogImage = ogImageUrl
                ? get(this.defaultValues, 'TRIDION_WEBDATA', '') + ogImageUrl
                : get(defaultMetadata, 'image', '');
            const ogSiteName = get(fbMeta, 'SiteName.Values[0]') ||
                get(defaultMetadata, 'facebookSiteName') ||
                '';
            const ogUrl = get(fbMeta, 'Url.Values[0]', canonicalUrl);
            this.meta.updateTag({ property: 'og:title', content: ogTitle });
            this.meta.updateTag({
                property: 'og:description',
                content: ogDescription,
            });
            this.meta.updateTag({ property: 'og:type', content: ogType });
            this.meta.updateTag({ property: 'og:image', content: ogImage });
            this.meta.updateTag({ property: 'og:site_name', content: ogSiteName });
            this.meta.updateTag({ property: 'og:url', content: ogUrl });
        }
        else {
            this.meta.updateTag({ property: 'og:url', content: canonicalUrl });
        }
        if (twitterMeta) {
            const twitterTitle = get(twitterMeta, 'Title.Values[0]') ||
                get(defaultMetadata, 'title') ||
                '';
            const twitterDescription = get(twitterMeta, 'Description.Values[0]') ||
                get(defaultMetadata, 'description') ||
                '';
            const twitterCreator = get(twitterMeta, 'Creator.Values[0]', '') ||
                get(defaultMetadata, 'twitterSiteName');
            const twitterSite = get(twitterMeta, 'Site.Values[0]') ||
                get(defaultMetadata, 'twitterSiteName') ||
                '';
            const twitterCard = get(twitterMeta, 'Card.KeywordValues[0].Key', '');
            const twitterImageUrl = get(twitterMeta, 'Image.LinkedComponentValues[0].Multimedia.Url', '');
            const twitterImage = get(defaultMetadata, 'image', '') ||
                get(this.defaultValues, 'TRIDION_WEBDATA', '') + twitterImageUrl;
            this.meta.updateTag({ name: 'twitter:title', content: twitterTitle });
            this.meta.updateTag({
                name: 'twitter:description',
                content: twitterDescription,
            });
            this.meta.updateTag({ name: 'twitter:creator', content: twitterCreator });
            this.meta.updateTag({ name: 'twitter:site', content: twitterSite });
            this.meta.updateTag({ name: 'twitter:card', content: twitterCard });
            this.meta.updateTag({ name: 'twitter:image', content: twitterImage });
        }
        if (location) {
            this.meta.updateTag({
                property: 'place:location:latitude',
                content: get(location, 'latitude', ''),
            });
            this.meta.updateTag({
                property: 'place:location:longitude',
                content: get(location, 'longitude', ''),
            });
        }
    }
}
SeoService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SeoService, deps: [{ token: i1.Meta }, { token: i1.Title }, { token: WINDOW }, { token: 'DefaultValues' }, { token: i2.LocaleService }], target: i0.ɵɵFactoryTarget.Injectable });
SeoService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SeoService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: SeoService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Meta }, { type: i1.Title }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['DefaultValues']
                }] }, { type: i2.LocaleService }]; } });

export { SeoService };
